import { RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { IBlock } from '@/lib/types';

import _chunk from 'lodash/chunk';

export const BLOCKS_PER_BATCH = 10;

export const NEXT_BATCH_PREFETCH_MARGIN = '600px 0px';

interface IUseBlockBatchesProps {
  blocks: IBlock[];
}

interface IUseBlockBatchesReturn {
  visibleBatches: IBlock[][];
  hasMoreBatches: boolean;
  sentinelRef: RefObject<HTMLDivElement | null>;
}

export const useBlockBatches = ({ blocks }: IUseBlockBatchesProps): IUseBlockBatchesReturn => {
  const batches = useMemo((): IBlock[][] => _chunk(blocks, BLOCKS_PER_BATCH), [blocks]);
  const [visibleBatchCount, setVisibleBatchCount] = useState(1);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const batchCount = batches.length;
  const hasMoreBatches = visibleBatchCount < batchCount;

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel || !hasMoreBatches) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisibleBatchCount(batchCount);

      return;
    }

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        if (!entries.some((entry: IntersectionObserverEntry) => entry.isIntersecting)) return;

        setVisibleBatchCount((current: number) => Math.min(current + 1, batchCount));
      },
      { rootMargin: NEXT_BATCH_PREFETCH_MARGIN }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [hasMoreBatches, batchCount, visibleBatchCount]);

  return {
    visibleBatches: batches.slice(0, visibleBatchCount),
    hasMoreBatches,
    sentinelRef,
  };
};
